// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React, { Component } from 'react';
import isEqual from 'lodash.isequal';
import Search from './Search';
import Scrollbars from 'react-custom-scrollbars';
import Loading from 'react-loading';

export default class SelectStack extends Component {

    state = {
        stacks: [],
        filtered: [],
        expanded: {},
        filter: ""
    }

    // Add a new prop for current stack ID
    componentDidMount = async () => {
        await this.loadData(this.props.stacks);
        this.preExpandAndScroll(this.props.currentStackId)
    }


    componentWillReceiveProps = (next) => {
        if (!isEqual(next.stacks, this.props.stacks) || !isEqual(next.value, this.props.value)){
            this.loadData(next.stacks);
        }
    }

    // Recursive subdirectory listing
    getSubDirs = (path, stacks) => {
        let subdirs = [];
        for (const stack of stacks) {
            if (!stack || !stack.path) continue;
            if (stack.path.startsWith(path + "/")) {
                // Ensure this is not sub-sub-dir...
                const pLen = path.split("/").length;
                const sLen = stack.path.split("/").length;
                if (pLen < sLen-1)
                    continue;

                subdirs.push({
                    ...stack, 
                    subdirs: this.getSubDirs(stack.path, stacks),
                });
            }
        }
        return subdirs;
    }

    preExpandAndScroll = (currentStackId) => {
        if (!currentStackId) return;

        const stack = this.props.stacks.find(s => s._id === currentStackId);
        if (!stack) return;

        const pathComponents = stack.path.split('/');
        let expanded = { ...this.state.expanded };
        
        // Pre-expand the parent folders
        let path = '';
        for (const component of pathComponents) {
            path += (path ? '/' : '') + component;
            // eslint-disable-next-line
            const stackToExpand = this.props.stacks.find(s => s.path === path);
            if (stackToExpand) {
                expanded[stackToExpand._id] = true;
            }
        }

        this.setState({ expanded }, () => {
            // Scroll to the current stack
            this.scrollToStack(currentStackId);
        });
    }

    scrollToStack = (stackId) => {
        const stackElement = document.getElementById(`stack-${stackId}`);
        
        if (stackElement) {
            // Scroll the stack into view
            stackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }
    
    loadData = (pStacks) => {
        // Selected stack
        if (!pStacks) return;

        const selected = this.props.isMulti ? pStacks.filter(s => this.props.value.includes(s._id)) : pStacks.find(s => s._id === this.props.value);

        // Go through stacks and re-organize into subgroups
        const stacks = [];
        const expanded = this.state.expanded;
        for (const stack of pStacks) {
            // Recursively list directory
            if (stack && stack.path && !stack.path.includes("/"))
                stacks.push({
                    ...stack, 
                    subdirs: this.getSubDirs(stack.path, pStacks),
                });

            // Expand by default?
            if (this.props.isMulti) {
                if (selected && selected.length > 0) {
                    for (const sel of selected)
                        if (sel.path.startsWith(stack.path + "/")) {
                            expanded[stack._id] = true;
                            break;
                        }
                }
            }
            else if (selected && selected.path.startsWith(stack.path + "/"))
                expanded[stack._id] = true;
        }

        this.setState({ stacks, expanded });
    }

    filterSubdirs = (stack, filter, expanded) => {
        let subdirs = [];
        for (const subdir of stack.subdirs) {
            const tmp = this.filterSubdirs(subdir, filter, expanded);
            if (subdir.path.toLowerCase().includes(filter.toLowerCase()) || tmp.length > 0) {
                subdirs.push({
                    _id: subdir._id,
                    path: subdir.path,
                    subdirs: tmp
                });
                if (tmp.length > 0)
                    expanded[subdir._id] = true;
            }
        }
        return subdirs;
    }

    filterStacks = (filter) => {
        const expanded = {};
        const filtered = this.filterSubdirs({
            path: "___root___",
            subdirs: this.state.stacks
        }, filter, expanded);
        this.setState({
            expanded: {
                ...this.state.expanded,
                ...expanded
            },
            filter,
            filtered
        });
    }

    renderStack = (stack) => (<React.Fragment key={stack._id}>
        <div
            id={`stack-${stack._id}`}
            className={"config-stack-stack" + ((this.props.isMulti ? this.props.value.includes(stack._id) : this.props.value === stack._id) ? " config-stack-selected" : "")}
            onClick={(e) => {
                if (!this.props.isMulti)
                {
                    // Unselect the stack if it is already selected
                    if (this.props.value === stack._id) 
                    {
                        return this.props.onChange(null);
                    }
                    return this.props.onChange(stack._id);
                }
                    

                let stacks = [ ...this.props.value ];

                // Control click?
                if (e.ctrlKey) {
                    // Sub stacks
                    const subStacks = this.props.stacks.filter(s => s.path.startsWith(stack.path));
                    
                    // Unselect
                    if (stacks.includes(stack._id)) {
                        for (const sub of subStacks) {
                            const stackIdx = stacks.findIndex(s => s === sub._id);
                            if (stackIdx >= 0)
                                stacks.splice(stackIdx, 1);
                        }
                    }

                    // Select
                    else {
                        for (const sub of subStacks)
                            if (!stacks.includes(sub._id))
                                stacks.push(sub._id);
                    }
                }

                else {
                    const stackIdx = stacks.findIndex(s => s === stack._id);

                    if (stackIdx >= 0)
                        stacks.splice(stackIdx, 1);
                        
                    else stacks.push(stack._id);
                }

                this.props.onChange(stacks);
            }}
            title={(this.props.isMulti ? "Click to toggle or Ctrl + click to select all recursive stacks inside " : "Click to select stack: ") + stack.path}
        >
            {/* Offset */}
            {stack.path.split("/").map((cmp,idx) => (idx ? <div
                className="config-stack-offset"
                key={idx}
            /> : ""))}

            {/* Dropdown arrow */}
            {stack.subdirs.length> 0 || this.props.dynamic? <div
                className="config-stack-dropdown-arrow"
                onClick={async(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    this.setState({
                        expanded: {
                            ...this.state.expanded,
                            [stack._id]: this.state.expanded[stack._id] ? false : true
                        }
                    })

                    if(this.props.dynamic){
                        // Do we need to load the children? 
                        const loading = {...this.state.loading??{}}; 
                        if(typeof stack.loadChildren === 'function' && !loading.hasOwnProperty(stack._id)){
                            loading[stack._id] = true;
                            this.setState({ loading });
                            await stack.loadChildren(); 
                        }
                        loading[stack._id] = false;
                        this.setState({ loading });
                    }
                }}
            >
                {this.state.expanded[stack._id] ? "▾" : "▸"}
            </div> : <div className="config-stack-no-dropdown">▾</div>}

            {/* Stack path */}
            <div className="config-stack-stack-path">
                {(() => {
                    const pathCmp = stack.path.split("/");
                    return pathCmp[pathCmp.length-1];
                })()}
                
                <span style={{color: '#787878'}}>{this.props.showFileCount? ` (${stack.numDocuments??0} files)`: ""}</span>
            </div>
        </div>
        
        {/* Dropdown */}
        {this.state.expanded[stack._id] && <div className="config-stack-expanded">

            {/* Are we loading the children of the stack? */}
            { this.state.loading?.[stack._id] ? 
                <>
                    <div
                        style={{display: 'flex'}}
                    >   
                        <span
                            style={{paddingLeft: 56, color: '#787878'}}
                        >
                            <Loading 
                                type="bubbles"
                                color="var(--color-primary)"
                                height={28}
                                width={28}
                            />
                        </span>
                      
                    </div>
                </>:""
            }
            
            {/* Has the stack been loaded and have no children? */}
            { !this.state.loading?.[stack._id] && stack.subdirs.length === 0 && this.props.dynamic?
            <>
                <div
                    style={{display: 'flex'}}
                >
                    <span style={{paddingLeft: 56, color: '#787878'}}>This folder is empty...</span>
                </div>
            </>
            :
            <>{stack.subdirs.map(substack => this.renderStack(substack))}</>
            }            
        </div>}
        
    </React.Fragment>);

    render = () => {
        return (<>
            {this.props.allowFilter ? <>
                <div style={{ marginLeft: -10, marginTop: -10 }}>
                    <Search
                        user={this.props.user}
                        query={this.state.filter}
                        search={this.filterStacks}
                        className="title-input doc-filter-search"
                        placeholder="Filter stacks by path..."
                        style={{
                            width: 225
                        }}
                    />
                </div>
                <div style={{ clear: 'both' }} />
            </> : ""}
            <div className="config-stack">
                <Scrollbars>
                    <div className="config-stack-container">
                        {(this.props.allowFilter && this.state.filter.length > 0 ? this.state.filtered : this.state.stacks).map(stack => this.renderStack(stack))}
                    </div>
                </Scrollbars>
            </div>
        </>);
    }

}